import React from 'react';
import { styled } from '@compiled/react';
import truncate from 'lodash/truncate';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { type PreloadedQuery, graphql, usePreloadedQuery, useQueryLoader } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import tooltipQuery, {
	type tooltipContentForVersionDetailsIssueCardFeatureFlagsQuery as tooltipQueryType,
} from '@atlassian/jira-relay/src/__generated__/tooltipContentForVersionDetailsIssueCardFeatureFlagsQuery.graphql';
import messages from './messages.tsx';

const MAX_DISPLAY_NAME_LENGTH = 32;
const MAX_FLAGS_TO_SHOW = 3;

type UseTooltipQueryType = (
	issueId: string,
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
) => [PreloadedQuery<tooltipQueryType> | null | void, () => void];

export const useTooltipContentQuery: UseTooltipQueryType = (issueId: string) => {
	const [queryRef, loadQuery] = useQueryLoader<tooltipQueryType>(tooltipQuery);
	return [queryRef, () => loadQuery({ issueId })];
};

type Props = {
	queryReference: PreloadedQuery<tooltipQueryType>;
};

export const TooltipContent = ({ queryReference }: Props) => {
	const { formatMessage } = useIntl();

	const result = usePreloadedQuery(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query tooltipContentForVersionDetailsIssueCardFeatureFlagsQuery($issueId: ID!) {
				devOps @required(action: THROW) {
					entitiesByAssociations(ids: [$issueId]) @required(action: THROW) {
						featureFlagEntities(first: 3) @required(action: THROW) {
							totalCount @required(action: THROW)
							edges @required(action: THROW) {
								node {
									id @required(action: NONE)
									displayName @required(action: NONE)
									summary @required(action: NONE) {
										enabled @required(action: NONE)
									}
								}
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	const { totalCount, edges } = result.devOps.entitiesByAssociations.featureFlagEntities;

	const featureFlags = edges
		?.flatMap((edge) => (edge?.node == null ? [] : [edge.node]))
		.map((node) => ({
			id: node.id,
			displayName:
				node.displayName.length > MAX_DISPLAY_NAME_LENGTH
					? truncate(node.displayName, {
							length: MAX_DISPLAY_NAME_LENGTH,
							separator: ' ',
						})
					: node.displayName,
			enabled: node.summary.enabled,
		}));

	const hiddenFlagsCount = Math.max(0, totalCount - MAX_FLAGS_TO_SHOW);

	return (
		<Box xcss={containerStyles}>
			<FeatureFlagList>
				{featureFlags.map(({ id, displayName, enabled }) => (
					<li key={id}>
						{enabled
							? formatMessage(messages.flagEnabled, { displayName })
							: formatMessage(messages.flagDisabled, { displayName })}
					</li>
				))}
			</FeatureFlagList>
			<MoreFlagsText>
				{hiddenFlagsCount > 0 ? formatMessage(messages.flagsHidden, { hiddenFlagsCount }) : null}
			</MoreFlagsText>
		</Box>
	);
};

const containerStyles = xcss({
	margin: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeatureFlagList = styled.ol({
	listStyle: 'none',
	paddingLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MoreFlagsText = styled.p({
	marginTop: token('space.050'),
});
